import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Shuffle, 
  Gift, 
  Users, 
  Clock, 
  Trophy,
  Loader2,
  Mail,
  Phone,
  User,
  Shield,
  MapPin,
  Crown,
  Hash,
  CheckCircle2,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";
import { publicAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import AnimatedFooter from "../components/AnimatedFooter";

// Generate a short reference from participant ID
const generateRef = (id) => {
  if (!id) return "000";
  return id.substring(0, 6).toUpperCase();
};

export default function PublicDraw() {
  const { id } = useParams();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [myRef, setMyRef] = useState(null);
  const [rgpdConsent, setRgpdConsent] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    loadDraw();
    const interval = setInterval(loadDraw, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const loadDraw = async () => {
    try {
      const res = await publicAPI.getDraw(id);
      setDraw(res.data);
    } catch (error) {
      toast.error("Tirage non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (data) => {
    setRegistering(true);
    try {
      const res = await publicAPI.registerToDraw(id, { ...data, rgpd_consent: rgpdConsent });
      toast.success("Inscription réussie !");
      setRegistered(true);
      if (res.data?.participant_id) {
        setMyRef(generateRef(res.data.participant_id));
      }
      reset();
      setRgpdConsent(false);
      loadDraw();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Card className="bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            <Shuffle className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Tirage non trouvé</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCompleted = draw.status === "completed";
  const participantCount = draw.participants?.length || draw.participants_count || 0;

  // Background style
  const bgStyle = draw.background_type === "image" && draw.background_image
    ? { backgroundImage: `url(${draw.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {};

  const bgColor = draw.background_color || '#1e3a5f';
  const accentColor = draw.accent_color || '#eab308';

  return (
    <div 
      className="min-h-screen"
      style={draw.background_type === "image" ? bgStyle : { background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 50%, ${bgColor}aa 100%)` }}
    >
      {draw.background_type === "image" && (
        <div className="absolute inset-0 bg-black/50" />
      )}
      <div className="relative z-10">
      {/* Header with big Logo */}
      <header className="pt-6 sm:pt-8 pb-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center">
            {draw.organizer_logo ? (
              <motion.img 
                src={draw.organizer_logo} 
                alt={draw.organizer_name}
                className="h-20 sm:h-28 md:h-36 object-contain mb-4 sm:mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              />
            ) : (
              <motion.div 
                className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 sm:mb-6 shadow-2xl"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Shuffle className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
              </motion.div>
            )}
            
            <motion.h1 
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-2 sm:mb-3 px-2"
              style={{ fontFamily: 'Outfit' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {draw.title}
            </motion.h1>
            
            <p className="text-white/70 text-base sm:text-lg mb-3 sm:mb-4">
              Organisé par <span className="text-white font-medium">{draw.organizer_name}</span>
            </p>
            
            {draw.description && (
              <p className="text-white/60 max-w-2xl text-center mb-3 sm:mb-4 text-sm sm:text-base px-2">
                {draw.description}
              </p>
            )}
            
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Badge className={`px-4 py-1.5 text-sm ${isCompleted ? "bg-green-500" : "bg-blue-500"} text-white`}>
                {isCompleted ? "Tirage terminé" : "Inscriptions ouvertes"}
              </Badge>
              
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Users className="w-4 h-4" />
                <span>{participantCount} participant{participantCount > 1 ? 's' : ''}</span>
              </div>
              
              {draw.draw_timestamp && (
                <div className="flex items-center gap-2 text-white/60 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{formatDate(draw.draw_timestamp)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-5xl mx-auto">
          {/* Prizes Section - Always visible */}
          {draw.prizes?.length > 0 && (
            <motion.div 
              className="mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6 flex items-center justify-center gap-2">
                <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
                Lots à gagner
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {draw.prizes.map((prize, index) => {
                  // Check if this prize has a winner
                  const winner = draw.results?.find(r => r.prize_id === prize.id);
                  
                  return (
                    <motion.div
                      key={prize.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * index }}
                    >
                      <Card className={`overflow-hidden ${
                        winner 
                          ? "bg-gradient-to-br from-yellow-500/30 to-amber-600/30 border-yellow-500/50" 
                          : "bg-white/10 border-white/20"
                      }`}>
                        <CardContent className="p-0">
                          {/* Prize Header */}
                          <div className={`p-4 ${winner ? "bg-gradient-to-r from-yellow-500/20 to-amber-500/20" : "bg-white/5"}`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                                winner 
                                  ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-yellow-500/30" 
                                  : "bg-white/10 text-white/70"
                              }`}>
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-white">{prize.name}</h3>
                                {prize.description && (
                                  <p className="text-white/60 text-sm mt-1">{prize.description}</p>
                                )}
                              </div>
                              <Badge className={`${winner ? "bg-yellow-500/30 text-yellow-200" : "bg-white/20 text-white"}`}>
                                x{prize.quantity}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Winner display if exists */}
                          {winner && (
                            <div className="p-4 border-t border-yellow-500/30">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                                    <Crown className="w-6 h-6 text-white" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs text-yellow-300/70 uppercase tracking-wider">Gagnant</p>
                                  <h4 className="text-lg font-bold text-white">
                                    {winner.participant_name}
                                  </h4>
                                  <p className="text-white/50 text-xs flex items-center gap-1">
                                    <Hash className="w-3 h-3" />
                                    Réf: {generateRef(winner.participant_id)}
                                  </p>
                                </div>
                                <Trophy className="w-6 h-6 text-yellow-400" />
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Registration Form or Completed Message */}
          <div className="flex justify-center">
            {isCompleted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <Card className="bg-white/10 border-white/20 backdrop-blur max-w-md">
                  <CardContent className="p-8">
                    <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Tirage terminé !</h3>
                    <p className="text-white/60 mb-6">
                      Le tirage a été effectué. Consultez les résultats ci-dessus.
                    </p>
                    <Link to={`/draw/${id}/results`}>
                      <Button className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Voir la page des résultats
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ) : registered ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <Card className="bg-green-500/20 border-green-500/50 backdrop-blur max-w-md">
                  <CardContent className="p-8">
                    <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Inscription confirmée !</h3>
                    <p className="text-white/70 mb-4">
                      Vous êtes bien inscrit(e) au tirage.
                    </p>
                    {myRef && (
                      <div className="bg-white/10 rounded-lg p-4 mb-4">
                        <p className="text-white/60 text-sm mb-1">Votre référence participant</p>
                        <p className="text-2xl font-mono font-bold text-white flex items-center justify-center gap-2">
                          <Hash className="w-5 h-5 text-green-400" />
                          {myRef}
                        </p>
                      </div>
                    )}
                    <p className="text-white/60 text-sm">
                      Conservez cette référence pour vérifier les résultats
                    </p>
                    <Link to={`/draw/${id}/results`} className="block mt-4">
                      <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Voir les résultats en direct
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
              >
                <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white text-center mb-6" style={{ fontFamily: 'Outfit' }}>
                      Inscrivez-vous au tirage
                    </h3>
                    
                    <form onSubmit={handleSubmit(onRegister)} className="space-y-4">
                      <div className="relative">
                        <User className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                        <Input
                          placeholder="Votre nom / pseudo *"
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10 focus:border-white/30"
                          data-testid="public-name-input"
                          {...register("name", { required: "Nom requis" })}
                        />
                        {errors.name && (
                          <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>
                        )}
                      </div>
                      
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                        <Input
                          type="email"
                          placeholder="Votre email"
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10 focus:border-white/30"
                          {...register("email")}
                        />
                      </div>
                      
                      <div className="relative">
                        <Phone className="w-4 h-4 absolute left-3 top-3.5 text-white/50" />
                        <Input
                          placeholder="Votre téléphone"
                          className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 pl-10 focus:border-white/30"
                          {...register("phone")}
                        />
                      </div>
                      
                      {/* RGPD Consent */}
                      <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/10">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="rgpd-consent"
                            checked={rgpdConsent}
                            onCheckedChange={setRgpdConsent}
                            className="mt-0.5 border-white/30 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                          />
                          <label htmlFor="rgpd-consent" className="text-sm text-white/70 cursor-pointer leading-relaxed">
                            J'accepte de recevoir des communications marketing de la part de l'organisateur. 
                            <span className="text-white/50"> (Optionnel)</span>
                          </label>
                        </div>
                        <p className="text-xs text-white/40 flex items-start gap-2">
                          <Shield className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>
                            Vos données sont protégées. Vous pouvez demander leur modification ou suppression 
                            à tout moment en contactant <a href="mailto:rgpd@tireo.app" className="text-white/60 underline hover:text-white/80">rgpd@tireo.app</a>
                          </span>
                        </p>
                      </div>
                      
                      <Button
                        type="submit"
                        className="w-full h-12 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold"
                        disabled={registering}
                        data-testid="public-register-btn"
                      >
                        {registering ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          "Participer au tirage"
                        )}
                      </Button>
                    </form>
                    
                    <p className="text-white/40 text-xs text-center mt-4">
                      Une référence unique vous sera attribuée après inscription
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 sm:p-6 text-center mt-auto">
        {draw.bailiff_name && (
          <div className="flex items-center justify-center gap-2 text-white/60 mb-2">
            <Shield className="w-4 h-4" />
            <span>Supervisé par {draw.bailiff_name}</span>
            {draw.bailiff_info && <span className="text-white/40">• {draw.bailiff_info}</span>}
          </div>
        )}
        {draw.withdrawal_instructions && (
          <div className="flex items-center justify-center gap-2 text-white/50 mb-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{draw.withdrawal_instructions}</span>
          </div>
        )}
        <AnimatedFooter variant="transparent" />
      </footer>
      </div>
    </div>
  );
}
